import React from 'react';
import TermsLayout from '../components/TermsLayout';

function CookiePolicy() {
  const sections = [
    {
      title: "1. Introduction",
      content: `This Cookie and Similar Technologies Policy ("Cookie Policy") explains how Zella Wellbeing ("Zella", "we", "us", or "our") uses cookies and similar technologies on our mobile application, web dashboard, and website (zellawellbeing.com).

"Similar technologies" includes local storage, session storage, and authentication tokens used in mobile applications, which serve similar purposes to cookies but are not technically cookies in the traditional sense.

This policy is primarily designed to comply with UK GDPR and the Privacy and Electronic Communications Regulations 2003 (PECR).

Related Policies:
- Privacy Policy: How we collect, store, and use your personal data
- Terms of Service: Your contractual relationship with Zella
- Data Processing Agreement: How we handle employee data on behalf of organisations`
    },
    {
      title: "2. Do We Need Cookie Consent?",
      content: `Zella currently uses only strictly necessary cookies and similar technologies. We do not use advertising, marketing, analytics, or profiling cookies.

Under UK PECR, strictly necessary cookies — those essential for a service explicitly requested by the user — are exempt from the consent requirement. All cookies and similar technologies Zella uses fall into this exempt category.

We therefore do not currently request cookie consent and do not display a cookie consent banner.

If optional cookies are introduced in future (for example, analytics or preference cookies), this policy and our cookie controls will be updated before those cookies are set, and we will implement appropriate consent mechanisms at that time.

Organisations deploying Zella to employees remain responsible for ensuring their employees receive any notices required under applicable data protection laws regarding the use of cookies or similar technologies on workplace devices.`
    },
    {
      title: "3. Technologies We Use",
      content: `The following is a complete list of cookie and similar technology categories currently used by Zella. Each item in this list describes actual technologies in use — we do not describe theoretical or planned technologies here.

MOBILE APPLICATION (Employee-Facing):

Authentication Token
Purpose: Maintain your login session between app uses so you do not need to re-enter your password every time
Technology: Secure local storage, managed by Firebase Authentication SDK
Retention: 30 days, after which re-login is required
Legal basis: Contract performance — required to provide the authenticated service you have requested
Can be disabled: No — the app cannot function without authentication

App Preference Storage
Purpose: Remember your in-app settings between sessions
Technology: Device local storage
Retention: Until the app is uninstalled or cache is cleared
Legal basis: Contract performance — required for a consistent app experience
Can be disabled: No — required for basic functionality

WEB DASHBOARD (Administrator-Facing):

Authentication Session
Purpose: Keep you securely logged in to the dashboard
Technology: HTTP-only secure cookie, managed by Firebase Authentication
Retention: Session (ends on browser close), or 30 days if "Remember Me" is selected
Legal basis: Contract performance — required to provide the authenticated service
Can be disabled: No — the dashboard cannot function without authentication

Dashboard Preference Storage
Purpose: Remember your dashboard display preferences (column visibility, filters, view settings)
Technology: Browser local storage
Retention: Until you clear browser site data
Legal basis: Legitimate interests — improving your experience of the service
Can be disabled: Yes — clearing browser site data removes these. The dashboard remains fully functional but will not remember your preferences.

WEBSITE (zellawellbeing.com):

Website Session
Purpose: Basic website functionality during your visit
Technology: Session cookie
Retention: Until browser closes
Legal basis: Contract performance
Can be disabled: No

THIRD-PARTY TECHNOLOGIES:

Google reCAPTCHA (registration step only)
Purpose: Prevent automated bot signups at the company administrator registration step
Technology: Third-party script operated by Google LLC. reCAPTCHA analyses device and interaction signals to distinguish humans from bots. It may set cookies or use local storage during this process.
Retention: Session / short-term
Legal basis: Legitimate interests in platform security and fraud prevention
Applies to: Company administrator registration only — not used anywhere else in the platform
International transfer: Google LLC (US) — UK-US Data Bridge
Can be disabled: No — required to complete company registration
Further information: https://policies.google.com/privacy

Stripe (payment flow only)
Purpose: Prevent fraud and authenticate payment sessions
Technology: Third-party cookies and signals set by Stripe, Inc. during the payment flow
Retention: Session
Legal basis: Contract performance — required for secure payment processing
Applies to: Paying customers only, during the payment flow only — not used in general platform use
International transfer: Stripe, Inc. (US) — UK-US Data Bridge
Can be disabled: No — required for payment processing
Further information: https://stripe.com/gb/privacy`
    },
    {
      title: "4. What We Do Not Do",
      content: `Zella does not itself engage in:
- Advertising or retargeting
- Cross-site tracking or behavioural profiling
- Browser fingerprinting for advertising or profiling purposes
- Social media tracking

We do not currently use analytics cookies or analytics platforms.

We do not sell data derived from cookies or similar technologies to any third party.

Note: The specific technologies listed in this policy may change as the product evolves. We will update this policy when material changes occur and will implement appropriate consent mechanisms before introducing any optional cookies.`
    },
    {
      title: "5. Your Choices",
      content: `Because Zella uses only strictly necessary technologies, there are no optional cookies to opt out of at this time.

You can clear cookies and local storage at any time:

Mobile app: Log out to clear the authentication session. Clear app cache via your device Settings → Apps → Zella → Clear Cache. Uninstalling the app removes all locally stored data.

Web dashboard: Log out to clear the authentication session. Use your browser's settings to clear cookies and site data for zellawellbeing.com. This will log you out and clear preference storage.

Website: Clear cookies for zellawellbeing.com in your browser settings.

Note: Clearing authentication cookies will log you out. The service will continue to function normally after you log back in.`
    },
    {
      title: "6. Security",
      content: `Authentication cookies set by Zella use industry-standard security attributes. Authentication tokens are generated and managed by Firebase Authentication. Encryption at rest and in transit is provided by Google Cloud infrastructure.

If you suspect unauthorised access to your session, log out immediately and contact security@zellawellbeing.com.`
    },
    {
      title: "7. Your Rights",
      content: `Under UK GDPR and PECR, you have the right to:
- Request information about cookies and similar technologies we use
- Request deletion of data associated with cookies (by clearing cookies or requesting account deletion)
- Object to processing based on legitimate interests

Because we use only strictly necessary technologies, the practical scope of these rights in relation to cookies is limited. The substantive data rights relating to your personal data are set out in our Privacy Policy.

To complain about our cookie practices:
Information Commissioner's Office (ICO)
Website: https://ico.org.uk
Phone: 0303 123 1113`
    },
    {
      title: "8. Changes to This Policy",
      content: `We will update this policy when:
- New cookies or similar technologies are introduced
- Existing technologies change materially
- Legal requirements change

Material changes will be communicated via email with 30 days notice. Minor changes (such as updating retention periods or clarifying descriptions) will be reflected in the "Last Updated" date.

Contact privacy@zellawellbeing.com with any questions.`
    }
  ];

  const summary = {
    title: "Summary",
    content: `Zella uses only strictly necessary cookies and similar technologies — authentication tokens, security cookies, and preference storage. We do not use advertising, analytics, or tracking cookies.

Because all our cookies are strictly necessary, we do not require your consent to set them under UK PECR.

Third parties: Google reCAPTCHA (registration step only), Stripe (payment flow only). Neither is used in general platform operation.

Your control: You can clear cookies and local storage at any time via browser or device settings. This will log you out but does not affect your account or data.

Questions? Email privacy@zellawellbeing.com`
  };

  return (
    <TermsLayout>
      <h1>Cookie and Similar Technologies Policy</h1>
      <p className="last-updated">Last Updated: June 2026</p>

      {sections.map((section, index) => (
        <section key={index}>
          <h2>{section.title}</h2>
          <p style={{ whiteSpace: 'pre-line' }}>{section.content}</p>
        </section>
      ))}

      <section className="summary-section">
        <h2>{summary.title}</h2>
        <p style={{ whiteSpace: 'pre-line' }}>{summary.content}</p>
      </section>

      <footer className="policy-footer">
        <p><strong>Version: 3.0</strong></p>
        <p>Effective Date: June 2026</p>
        <p>Complies with: UK GDPR, Privacy and Electronic Communications Regulations 2003 (PECR)</p>
        <p><em>Zella — Workplace Wellbeing Monitoring</em></p>
      </footer>
    </TermsLayout>
  );
}

export default CookiePolicy;